import React from 'react';
import styled from 'styled-components/macro';
import { FormikProps, FieldArray } from 'formik';
import { useOnMount } from '../../../helpers/hooks/useOnMount';
import { PrimaryProgressButton, SecondaryButton } from '../../../shared/Button';
import { Form, FormButtonRow } from '../../../shared/form/Form';
import { DataForEditPdfDetailsForm } from './dataForEditPdfDetailsForm';
import { EditPdfDetailsFormModel } from './editPdfDetailsFormModel';
import { TextInputField } from '../../../shared/form/inputs/TextInputField';
import { colourNeutral09, colourNeutral10 } from '../../../styling/colours';
import { DateInputField } from '../../../shared/form/inputs/DateInputField';
import { FormFieldRow } from '../../../shared/form/FormFieldRow';
import { RevisionSelectField } from '../../shared/quote-or-design-form/RevisionSelectField';
import { RevisionDto } from './RevisionDto';

export type NextStepAfterSavingPdfDetails = 'ViewPdf' | 'BackToDesignDrawings';

type Props = {
  data: DataForEditPdfDetailsForm;
  setNextStepAfterSave: (nextStep: NextStepAfterSavingPdfDetails) => void;
} & FormikProps<EditPdfDetailsFormModel>;

const MAX_REVISIONS = 3;

export const EditPdfDetailsForm = ({ isSubmitting, validateForm, values, data, setNextStepAfterSave }: Props) => {
  useOnMount(() => {
    void validateForm(values);
  });

  const blankRevision: RevisionDto = { code: '', date: '', description: '', initials: '' };

  return (
    <Form>
      <FormFieldRow>
        <TextInputField name="numberOfPagesNotIncluded" label="Number of Pages Not Included" />
        <TextInputField name="designerInitials" label="Designer Initials" />
        <TextInputField name="drawerInitials" label="Drawn (Initials)" />
        <TextInputField name="waterBufferSizeInLitres" label="Water Buffer Size In Litres" />
        {data.heatSource === 'Boiler' && (
          <TextInputField name="boilerCapacityInKilowatts" label="Boiler Size In Kilowatts" />
        )}
      </FormFieldRow>
      <FieldArray name="revisions">
        {({ push, remove }) => (
          <>
            {values.revisions.length > 0 &&
              values.revisions.map((revision, index) => (
                <RevisionFieldset key={index}>
                  <RevisionHeader>
                    <RevisionSelectField name={`revisions.${index}.code`} label={null} />
                    <SecondaryButton onClick={() => remove(index)}>Remove Revision</SecondaryButton>
                  </RevisionHeader>
                  <FormFieldRow>
                    <DateInputField name={`revisions.${index}.date`} label="Date" />
                    <TextInputField name={`revisions.${index}.initials`} label="Initials" />
                    <TextInputField name={`revisions.${index}.description`} label="Description" />
                  </FormFieldRow>
                </RevisionFieldset>
              ))}
            {values.revisions.length < MAX_REVISIONS && (
              <SecondaryButton onClick={() => push(blankRevision)}>Add Revision</SecondaryButton>
            )}
          </>
        )}
      </FieldArray>
      <FormButtonRow $align="left">
        <PrimaryProgressButton
          type="submit"
          inProgress={isSubmitting}
          onClick={() => setNextStepAfterSave('BackToDesignDrawings')}
        >
          Save
        </PrimaryProgressButton>
        <PrimaryProgressButton type="submit" inProgress={isSubmitting} onClick={() => setNextStepAfterSave('ViewPdf')}>
          Save and View
        </PrimaryProgressButton>
      </FormButtonRow>
    </Form>
  );
};

const RevisionFieldset = styled.fieldset`
  border: 1px solid ${colourNeutral09};
  padding: 10px;
  background-color: ${colourNeutral10};
  margin-bottom: 20px;
`;

const RevisionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;
